import { getWorkoutSetScope, workoutSetScopeKey } from './workoutSetScope';

const STORAGE_KEY = 'kata.workoutSetOutbox.v2';
const LEGACY_STORAGE_KEY = 'kata.workoutSetOutbox.v1';
const FLUSH_DELAY_MS = 500;
const MAX_ENTRY_AGE_MS = 24 * 60 * 60 * 1000;
const RETRY_DELAYS_MS = [1000, 3000, 8000, 20000, 45000];

const isRetryable = (error) => {
  const status = error?.response?.status;
  return !status || status === 401 || status === 408 || status === 425 || status === 429 || status >= 500;
};

const sameUpdates = (a, b) => {
  const aKeys = Object.keys(a || {});
  const bKeys = Object.keys(b || {});
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => JSON.stringify(a[key]) === JSON.stringify(b[key]));
};

const principalKeyFor = (context = {}) => JSON.stringify([context.workoutSetIdentity ?? null, context.workoutSetOwner ?? null]);
const samePrincipal = (a, b) => principalKeyFor(a) === principalKeyFor(b);

const pendingKeyFor = (scope, updates, context = {}) => {
  const baseKey = workoutSetScopeKey(scope);
  const scopeKey = baseKey && (context.workoutSetIdentity || context.workoutSetOwner)
    ? `${baseKey}:principal:${principalKeyFor(context)}` : baseKey;
  if (!scopeKey || !context.separateWorkoutSetFields) return scopeKey;
  const fields = Object.keys(updates || {});
  if (fields.length !== 1) return null;
  return `${scopeKey}:field:${fields[0]}`;
};

class WorkoutSetOutbox {
  constructor(sendUpdate) {
    this.sendUpdate = sendUpdate;
    this.pending = this.load();
    // Migrate any previously stored coordinates away immediately.
    this.persist();
    this.waiters = new Map();
    this.drainWaiters = new Set();
    this.flushTimer = null;
    this.flushing = false;
    this.flushQueued = false;
    this.retryAttempt = 0;
    this.bindLifecycle();
    if (this.pending.size) this.scheduleFlush(2000);
  }

  bindLifecycle() {
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => this.flush());
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flush());
      window.addEventListener('online', () => {
        this.retryAttempt = 0;
        this.flush();
      });
    }
  }

  load() {
    const entries = new Map();
    try {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return entries;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return entries;
      const now = Date.now();
      parsed.forEach(entry => {
        const scope = getWorkoutSetScope(entry?.setId, entry?.context);
        const key = pendingKeyFor(scope, entry?.updates, entry?.context);
        if (!entry?.updates || !key || now - (entry.queuedAt || 0) > MAX_ENTRY_AGE_MS) return;
        entries.set(key, {
          setId: scope.setId,
          context: { ...entry.context, eventPosition: null, workoutId: scope.activityId, workoutExerciseId: scope.workoutExerciseId },
          updates: entry.updates,
          queuedAt: entry.queuedAt || now
        });
      });
    } catch (error) {
      console.warn('Could not restore workout set outbox:', error);
    }
    return entries;
  }

  persist() {
    try {
      if (!this.pending.size) {
        window.localStorage.removeItem(STORAGE_KEY);
        return;
      }
      // Keep original device snapshots only in the live queue. A restored edit
      // has no admissible location and must never borrow a newly warmed fix.
      const entries = Array.from(this.pending.values(), entry => ({
        ...entry, context: { ...entry.context, eventPosition: null }
      }));
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (error) {
      console.warn('Could not persist workout set outbox:', error);
    }
  }

  clearEventPositions() {
    this.pending.forEach(entry => { entry.context = { ...entry.context, eventPosition: null }; });
    this.persist();
  }

  enqueue(setId, updates, { immediate = false, context = {} } = {}) {
    const scope = getWorkoutSetScope(setId, context);
    const key = pendingKeyFor(scope, updates, context);
    if (!key) {
      const message = context.separateWorkoutSetFields
        ? 'A separated workout set update must contain exactly one field.'
        : 'Workout set update is missing workout scope.';
      return Promise.reject(new Error(message));
    }

    const existing = this.pending.get(key);
    this.pending.set(key, {
      setId: scope.setId,
      context: { ...context, workoutId: scope.activityId, workoutExerciseId: scope.workoutExerciseId },
      updates: { ...(existing?.updates || {}), ...updates },
      queuedAt: existing ? existing.queuedAt : Date.now()
    });
    this.persist();

    const promise = new Promise((resolve, reject) => {
      const waiters = this.waiters.get(key) || [];
      waiters.push({ resolve, reject });
      this.waiters.set(key, waiters);
    });

    if (immediate) this.flush();
    else this.scheduleFlush(FLUSH_DELAY_MS);
    return promise;
  }

  scheduleFlush(delay) {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delay);
  }

  getPendingUpdates(setId, context) {
    const key = workoutSetScopeKey(getWorkoutSetScope(setId, context));
    let updates = {};
    for (const entry of this.pending.values()) {
      if (workoutSetScopeKey(getWorkoutSetScope(entry.setId, entry.context)) === key && samePrincipal(entry.context, context)) {
        updates = { ...updates, ...entry.updates };
      }
    }
    return updates;
  }

  async waitForExercise(context) {
    const matches = entry => entry.context.workoutId === String(context.workoutId)
      && entry.context.workoutExerciseId === String(context.workoutExerciseId) && samePrincipal(entry.context, context);
    while ([...this.pending.values()].some(matches)) {
      const writes = [...this.pending].filter(([, entry]) => matches(entry)).map(([key]) =>
        new Promise((resolve, reject) => {
          this.waiters.set(key, [...(this.waiters.get(key) || []), { resolve, reject }]);
        }));
      this.flush();
      await Promise.all(writes);
    }
  }

  hasPendingForWorkout(workoutId, context) {
    return [...this.pending.values()].some(entry => entry.context.workoutId === String(workoutId) && samePrincipal(entry.context, context));
  }

  hasPending() {
    return this.pending.size > 0 || this.flushing || this.flushQueued;
  }

  // Observe the existing retry lifecycle without flushing, polling, or changing
  // backoff. Automatic Coach intent lives until actual drain or scope abort.
  waitForDrain(signal) {
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        this.drainWaiters.delete(onDrain);
        signal?.removeEventListener('abort', onAbort);
      };
      const onDrain = () => { cleanup(); resolve(); };
      const onAbort = () => {
        cleanup();
        reject(Object.assign(new Error('Workout review cancelled.'), { name: 'AbortError' }));
      };
      if (signal?.aborted) { onAbort(); return; }
      if (!this.hasPending()) { resolve(); return; }
      this.drainWaiters.add(onDrain);
      signal?.addEventListener('abort', onAbort, { once: true });
    });
  }

  async waitForPending(timeoutMs = 5000) {
    if (!this.hasPending()) return;
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
      this.flush();
    }
    const deadline = Date.now() + timeoutMs;
    while (this.hasPending()) {
      if (Date.now() > deadline) throw new Error('Timed out waiting for workout set edits to save.');
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  async flush() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.flushing) {
      this.flushQueued = true;
      return;
    }
    if (!this.pending.size) {
      if (!this.hasPending()) [...this.drainWaiters].forEach(notify => notify());
      return;
    }

    this.flushing = true;
    let retryNeeded = false;
    const blockedSeparatedScopes = new Set();
    try {
      for (const key of Array.from(this.pending.keys())) {
        const entry = this.pending.get(key);
        if (!entry) continue;
        const entryScope = getWorkoutSetScope(entry.setId, entry.context);
        const separatedScopeKey = entry.context?.separateWorkoutSetFields
          ? workoutSetScopeKey(entryScope)
          : null;
        if (separatedScopeKey && blockedSeparatedScopes.has(separatedScopeKey)) {
          retryNeeded = true;
          continue;
        }
        const waiters = this.waiters.get(key) || [];
        this.waiters.set(key, []);
        try {
          const result = await this.sendUpdate(entry.setId, entry.updates, { context: entry.context });
          this.retryAttempt = 0;
          const current = this.pending.get(key);
          const currentWaiters = this.waiters.get(key) || [];
          if (current && sameUpdates(current.updates, entry.updates)) {
            this.pending.delete(key);
            this.waiters.delete(key);
            [...waiters, ...currentWaiters].forEach(({ resolve }) => resolve(result));
          } else if (current) {
            retryNeeded = true;
            if (separatedScopeKey) blockedSeparatedScopes.add(separatedScopeKey);
            waiters.forEach(({ resolve }) => resolve(result));
          } else {
            this.waiters.delete(key);
            [...waiters, ...currentWaiters].forEach(({ resolve }) => resolve(result));
          }
          this.persist();
        } catch (error) {
          if (isRetryable(error)) {
            retryNeeded = true;
            if (separatedScopeKey) blockedSeparatedScopes.add(separatedScopeKey);
            this.waiters.set(key, [...waiters, ...(this.waiters.get(key) || [])]);
          } else {
            const current = this.pending.get(key);
            const currentWaiters = this.waiters.get(key) || [];
            if (current && !sameUpdates(current.updates, entry.updates)) {
              retryNeeded = true;
              if (separatedScopeKey) blockedSeparatedScopes.add(separatedScopeKey);
              waiters.forEach(({ reject }) => reject(error));
            } else {
              this.pending.delete(key);
              this.waiters.delete(key);
              [...waiters, ...currentWaiters].forEach(({ reject }) => reject(error));
            }
            this.persist();
          }
        }
      }
    } finally {
      this.flushing = false;
    }

    if (this.flushQueued) {
      this.flushQueued = false;
      this.flush();
      return;
    }
    if (retryNeeded && this.pending.size) {
      const delay = RETRY_DELAYS_MS[Math.min(this.retryAttempt, RETRY_DELAYS_MS.length - 1)];
      this.retryAttempt += 1;
      this.scheduleFlush(delay);
    }
    if (!this.hasPending()) [...this.drainWaiters].forEach(notify => notify());
  }
}

export default WorkoutSetOutbox;
